import type { TxLink } from 'components/link/TxDetail';
import type {
  GetExternalAccountLinkedAccountsQuery,
  GetExternalAccountLinkedAccountsQueryVariables,
  GetExternalAccountTxsQuery,
  GetExternalAccountTxsQueryVariables,
} from 'hooks/customerApi';
import type { FC } from 'react';
import type { AnyVariables, UseQueryExecute, UseQueryState } from 'urql';
import type { DefaultParams, Params } from 'wouter';
import { DetailPane } from 'components/detail-pane/DetailPane';
import { ExternalAccountSummary } from 'components/link/ExternalAccountSummary';
import { LinkedLedgerAccountSelector } from 'components/link/LinkedLedgerAccountSelector';
import { TxDetail } from 'components/link/TxDetail';
import { TxsTable } from 'components/link/TxsTable/TxsTableV1';
import { type LinkedLedgerAccount, type TxNode } from 'components/link/types';
import { TableLoadError } from 'components/TableLoadError/TableLoadError';
import {
  GetExternalAccountLinkedAccountsDocument,
  GetExternalAccountTxsDocument,
  useGetExternalAccountSummaryQuery,
  useGetTxLinesQuery,
} from 'hooks/customerApi';
import { useCustomerApiContext } from 'hooks/useCustomerApiContext';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useHasWorkspacePermission } from 'hooks/useHasWorkspacePermission';
import React, { useCallback, useMemo, useState, useTransition } from 'react';
import { getResultOrError } from 'utils/errors';
import { makePath } from 'utils/routes';
import { usePaginatedQuery } from 'utils/urql';
import { useLocation } from 'wouter';
import { useWorkspaceId } from 'wrappers/WorkspaceProvider';

import { Permissions } from '@fragment/authorization-utils/permissions';
import { PrimaryButtonV2 } from '@fragment/ui/src/components/Button/PrimaryButtonV2/PrimaryButtonV2';
import { TextButton } from '@fragment/ui/src/components/Button/TextButton/TextButton';
import { ErrorText } from '@fragment/ui/src/components/ErrorText';
import { Icon } from '@fragment/ui/src/components/Icon';

import { IntegrationSettingsLayout } from './IntegrationSettingsLayout';
import { SyncCustomTxModal } from './SyncCustomTxModal';

const ExternalAccountTxsTable = React.memo(
  ({
    accountId,
    ledgerAccountId,
    linkId,
    txId,
    selected,
    updateSelectedRow,
    response,
    reExecuteQuery,
    data,
    paginateNext,
  }: {
    accountId: string;
    ledgerAccountId: string | undefined;
    linkId: string;
    txId: string | undefined;
    selected?: TxNode;
    updateSelectedRow: (tx: TxNode) => void;
    response: UseQueryState<GetExternalAccountTxsQuery, AnyVariables>;
    reExecuteQuery: UseQueryExecute;
    data: TxNode[];
    paginateNext: () => void;
  }) => {
    const [isPending, startTransition] = useTransition();

    const selectedRow: TxNode | undefined = txId
      ? data.find((tx) => tx.id === txId)
      : undefined;

    const loadMore = useCallback(
      () => startTransition(paginateNext),
      [paginateNext]
    );
    const getPath = useCallback(
      (row: TxNode) =>
        makePath(['links', 'id', linkId, 'accounts', accountId, 'tx', row.id]),
      [accountId, linkId]
    );
    const { isError } = getResultOrError(
      response.data?.externalAccount,
      response.error
    );

    const retry = useCallback(
      () => reExecuteQuery({ requestPolicy: 'network-only' }),
      [reExecuteQuery]
    );

    if (selectedRow) {
      updateSelectedRow(selectedRow);
    }
    return isError ? (
      <TableLoadError
        entityColumnName="Transaction"
        {...{
          error: response.error,
          retry,
          fetching: response.fetching,
        }}
      />
    ) : (
      <TxsTable
        ledgerAccountId={ledgerAccountId}
        data={data}
        selected={selected}
        getPath={getPath}
        hasNextPage={response.data?.externalAccount?.txs.pageInfo.hasNextPage}
        loadMore={loadMore}
        isLoading={isPending}
      />
    );
  }
);

type TxDetailLoaderProps = {
  link: TxLink;
  ledgerAccount: LinkedLedgerAccount | undefined;
  txId: string;
  selectedRow: TxNode | undefined;
};

export const TxDetailLoader: FC<TxDetailLoaderProps> = ({
  link,
  ledgerAccount,
  txId,
  selectedRow,
}) => {
  const { context } = useCustomerApiContext();
  const variables = useMemo(() => ({ txId }), [txId]);
  const [{ data, error, fetching }, reExecuteQuery] = useGetTxLinesQuery({
    context,
    pause: !context.token || !!selectedRow,
    variables,
  });
  const retry = useCallback(
    () => reExecuteQuery({ requestPolicy: 'network-only' }),
    [reExecuteQuery]
  );
  if (selectedRow) {
    return <TxDetail link={link} tx={selectedRow} account={ledgerAccount} />;
  }
  const { isError, result } = getResultOrError(data?.tx, error);
  if (isError) {
    return (
      <div className="w-full">
        <TableLoadError
          retry={retry}
          entityColumnName="Transaction Description"
          fetching={fetching}
        />
      </div>
    );
  }
  return <TxDetail link={link} tx={result} account={ledgerAccount} />;
};

type Props<T extends DefaultParams> = {
  params: Params<T>;
};

export const ExternalAccountDetailView = <T extends DefaultParams>({
  params: { linkId, accountId, txId },
}: Props<T>) => {
  const { context, token } = useCustomerApiContext();
  const workspaceId = useWorkspaceId();
  const canAddTx = useHasWorkspacePermission(Permissions.link.sync);
  const [, setLocation] = useLocation();
  const extAcctSummaryVariables = useMemo(
    () => ({
      id: accountId,
    }),
    [accountId]
  );
  const [queryResult, reExecuteQuery] = useGetExternalAccountSummaryQuery({
    context,
    pause: !context.token,
    variables: extAcctSummaryVariables,
  });
  const { isError, result: externalAccount } = getResultOrError(
    queryResult.data?.externalAccount,
    queryResult.error
  );

  const retry = useCallback(
    () => reExecuteQuery({ requestPolicy: 'network-only' }),
    [reExecuteQuery]
  );
  useDocumentTitle(
    queryResult.error || queryResult.fetching
      ? 'External Account'
      : `External Account - ${externalAccount?.name}`
  );
  const [selectedRow, updateSelectedRow] = useState<TxNode>();

  const [selected, setSelected] = useState<LinkedLedgerAccount>();
  const extAcctLinkedAccountVariables = useMemo(
    () => ({
      id: accountId,
    }),
    [accountId]
  );
  const {
    response: listAccountsResponse,
    reExecuteQuery: reExecuteAccountsQuery,
    data,
    paginateNext,
  } = usePaginatedQuery<
    LinkedLedgerAccount,
    GetExternalAccountLinkedAccountsQuery,
    GetExternalAccountLinkedAccountsQueryVariables
  >({
    getConnection: ({ externalAccount: extAcct }) => extAcct?.ledgerAccounts,
    queryDocument: GetExternalAccountLinkedAccountsDocument,
    queryOptions: {
      context,
      pause: !context.token,
    },
    variables: extAcctLinkedAccountVariables,
  });
  const [isPending, startTransition] = useTransition();
  const loadMore = useCallback(() => {
    startTransition(paginateNext);
  }, [paginateNext]);

  const { isError: isLinkedAccountError } = getResultOrError(
    listAccountsResponse.data?.externalAccount,
    listAccountsResponse.error
  );

  const getPath = useCallback(
    (account: LinkedLedgerAccount) =>
      makePath(
        [
          'w',
          workspaceId,
          'projects',
          ...(account.ledger.schema?.key
            ? ['schema', account.ledger.schema.key]
            : []),
          account.ledgerId,
          'accounts',
          account.id,
        ],
        {
          trailingSlash: true,
          absolute: true,
        }
      ),
    [workspaceId]
  );

  const retryListAccounts = useCallback(
    () => reExecuteAccountsQuery({ requestPolicy: 'network-only' }),
    [reExecuteAccountsQuery]
  );

  // Typescript doesn't infer that this can be undefined, so I have to help it along.
  const selectedAccount: LinkedLedgerAccount | undefined =
    selected ?? (data[0] as LinkedLedgerAccount | undefined);
  const accountSelector = isLinkedAccountError ? (
    <TableLoadError
      entityColumnName="Account"
      {...{
        error: listAccountsResponse.error,
        retry: retryListAccounts,
        fetching: listAccountsResponse.fetching,
      }}
    />
  ) : (
    <LinkedLedgerAccountSelector
      accounts={data}
      setSelected={setSelected}
      selected={selectedAccount}
      loadMore={
        listAccountsResponse.data?.externalAccount?.ledgerAccounts.pageInfo
          .hasNextPage
          ? loadMore
          : undefined
      }
      fetching={isPending}
      getPath={getPath}
    />
  );

  const [showSyncCustomTxModal, setShowSyncCustomTxModal] = useState(false);

  // tx table data
  const {
    response: responseTxTable,
    reExecuteQuery: reExecuteQueryTxTable,
    data: dataTxTable,
    paginateNext: paginateNextTxTable,
  } = usePaginatedQuery<
    TxNode,
    GetExternalAccountTxsQuery,
    GetExternalAccountTxsQueryVariables
  >({
    getConnection: ({ externalAccount: externalAccountTx }) =>
      externalAccountTx?.txs,
    queryDocument: GetExternalAccountTxsDocument,
    queryOptions: {
      context,
      pause: !token,
    },
    variables: extAcctLinkedAccountVariables,
  });

  if (isError) {
    return (
      <IntegrationSettingsLayout>
        <div className="flex justify-between gap-2">
          <ErrorText>Error fetching External Account details</ErrorText>
          <TextButton inverted onClick={retry}>
            Retry <Icon type="right" />
          </TextButton>
        </div>
      </IntegrationSettingsLayout>
    );
  }

  const shouldRenderAddTxCTA =
    canAddTx && externalAccount.link.__typename === 'CustomLink';

  return (
    <IntegrationSettingsLayout
      summarySection={
        <ExternalAccountSummary
          externalAccountId={externalAccount.externalId}
          externalAccountName={externalAccount.name}
          currency={externalAccount.currency}
          linkType={externalAccount.link.__typename}
          accountSelector={accountSelector}
          currencyMode={externalAccount.currencyMode}
        />
      }
      summarySectionActions={
        shouldRenderAddTxCTA && (
          <PrimaryButtonV2 onClick={() => setShowSyncCustomTxModal(true)}>
            Add Tx
            <span className="text-main-500">+</span>
          </PrimaryButtonV2>
        )
      }
      detailSideBar={
        txId && (
          <DetailPane.Container loadingText="Loading Tx">
            <TxDetailLoader
              txId={txId}
              link={externalAccount.link}
              ledgerAccount={selectedAccount}
              selectedRow={selectedRow}
            />
          </DetailPane.Container>
        )
      }
      onDetailSideBarClose={() => {
        updateSelectedRow(undefined);
        setLocation(
          makePath(
            [
              'w',
              workspaceId,
              'projects',
              'settings',
              'integrations',
              'links',
              'id',
              linkId,
              'accounts',
              accountId,
            ],
            { absolute: true }
          )
        );
      }}
    >
      <ExternalAccountTxsTable
        ledgerAccountId={selectedAccount?.id}
        accountId={accountId}
        linkId={linkId}
        txId={txId}
        selected={selectedRow}
        updateSelectedRow={updateSelectedRow}
        response={responseTxTable}
        reExecuteQuery={reExecuteQueryTxTable}
        data={dataTxTable}
        paginateNext={paginateNextTxTable}
      />
      {showSyncCustomTxModal && (
        <SyncCustomTxModal
          externalAccount={externalAccount}
          refresh={() =>
            setTimeout(() => {
              retry();
              reExecuteQueryTxTable({ requestPolicy: 'network-only' });
            }, 1000)
          }
          onClose={() => setShowSyncCustomTxModal(false)}
          linkId={linkId}
        />
      )}
    </IntegrationSettingsLayout>
  );
};
